import { v2 } from "../../lib/api-v2";
import type {
  AgentConfigResponse,
  AnalyticsSummary,
  CallDetail,
  CallsPage,
  ConfigResponse,
  FeatureInfo,
  FeatureSettings,
  ImprovementRun,
  Scorecard,
  ScorecardResponse,
  ScopeDoc,
} from "./types";

// Core proxies /api/v2/call-analytics/<path> to the service's /v1/<path> after auth and RBAC.
const BASE = "/call-analytics";
const id = encodeURIComponent;

const query = (params: Record<string, string | number | null | undefined>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

export type RangeParams = {
  from: string;
  to: string;
  agent_id?: string | null;
};

export const callAnalyticsApi = {
  features: () => v2.get<{ features: FeatureInfo[] }>(`${BASE}/features`),

  listCalls: (params: RangeParams & { limit?: number; cursor?: string | null }) =>
    v2.get<CallsPage>(`${BASE}/calls${query(params)}`),
  getCall: (callId: string) => v2.get<CallDetail>(`${BASE}/calls/${id(callId)}`),
  analyseCall: (callId: string) => v2.post<{ queued: boolean }>(`${BASE}/calls/${id(callId)}/analyse`, {}),
  summary: (params: RangeParams) => v2.get<AnalyticsSummary>(`${BASE}/summary${query(params)}`),

  config: () => v2.get<ConfigResponse>(`${BASE}/config`),
  agentConfig: (agentId: string) => v2.get<AgentConfigResponse>(`${BASE}/config/agents/${id(agentId)}`),
  saveOrganizationConfig: (features: FeatureSettings) =>
    v2.put<ScopeDoc>(`${BASE}/config/organization`, { features }),
  saveAgentConfig: (agentId: string, features: FeatureSettings) =>
    v2.put<ScopeDoc>(`${BASE}/config/agents/${id(agentId)}`, { features }),
  deleteAgentConfig: (agentId: string) => v2.delete<void>(`${BASE}/config/agents/${id(agentId)}`),

  scorecard: (agentId?: string | null) => v2.get<ScorecardResponse>(`${BASE}/scorecard${query({ agent_id: agentId })}`),
  saveOrganizationScorecard: (scorecard: Scorecard) => v2.put<Scorecard>(`${BASE}/scorecard`, scorecard),
  saveAgentScorecard: (agentId: string, scorecard: Scorecard) =>
    v2.put<Scorecard>(`${BASE}/scorecard/agents/${id(agentId)}`, scorecard),
  deleteAgentScorecard: (agentId: string) => v2.delete<void>(`${BASE}/scorecard/agents/${id(agentId)}`),

  listImprovements: (agentId: string) =>
    v2.get<{ items: ImprovementRun[] }>(`${BASE}/agents/${id(agentId)}/improvements`),
  startImprovement: (agentId: string) =>
    v2.post<ImprovementRun>(`${BASE}/agents/${id(agentId)}/improvements`, {}),
  getImprovement: (runId: string) => v2.get<ImprovementRun>(`${BASE}/improvements/${id(runId)}`),

  // TechLadder admins; core resolves the organization the user owns.
  adminConfig: (userId: string) => v2.get<ConfigResponse>(`${BASE}/admin/users/${id(userId)}/config`),
  saveAdminConfig: (userId: string, features: FeatureSettings) =>
    v2.put<ScopeDoc>(`${BASE}/admin/users/${id(userId)}/config`, { features }),
};

export const errorText = (error: unknown, fallback = "Something went wrong.") =>
  error instanceof Error && error.message ? error.message : fallback;
